import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { filter, orderBy } from 'lodash';
import moment from 'moment';

import { Employee, EmployeeStatusEnum } from '@pimm/services/lib/sms-workforce';

export type CalendarEventType = 'Birthday' | 'Anniversary';

export interface CalendarEvent<T = unknown> {
  dateTime: Date;
  type: CalendarEventType;
  referenceId?: string;
  name?: string;
  description?: string;
  data?: T;
}

export interface CalendarEventsContextReturn {
  events: CalendarEvent[];
  getEventsByDate: (today: Date) => CalendarEvent[];
}

export const CalendarEventsContext = createContext<CalendarEventsContextReturn>(undefined!);

export type CalendarEventsProviderProps = PropsWithChildren & {
  employees?: Employee[];
};

export const CalendarEventsProvider = ({ children, employees }: CalendarEventsProviderProps) => {
  const events = useMemo(() => {
    const employeeEvents: CalendarEvent[] = [];

    for (const { birthDate, hireDate, firstName, lastName, ...employee } of employees ?? []) {
      if (employee.status !== EmployeeStatusEnum.Active) continue;

      const event: Partial<CalendarEvent> = {
        data: { firstName, lastName },
        referenceId: employee.userId ?? employee.id,
        name: firstName ? `${firstName} ${lastName}` : employee.userName,
      };

      if (birthDate) {
        const dateTime = moment(birthDate?.slice(0, 10)).toDate();
        employeeEvents.push({ ...event, type: 'Birthday', dateTime } as CalendarEvent);
      }

      if (hireDate) {
        const dateTime = moment(hireDate?.slice(0, 10)).toDate();
        employeeEvents.push({ ...event, type: 'Anniversary', dateTime } as CalendarEvent);
      }
    }

    return orderBy(employeeEvents, ['dateTime']);
  }, [employees]);

  // prettier-ignore
  const getEventsByDate = useCallback((today: Date) => {
    return filter(events, ({ dateTime }) => {
      return dateTime.getFullYear() < today.getFullYear() && dateTime.getMonth() === today.getMonth() && dateTime.getDate() === today.getDate()
    });
  }, [events]);

  return (
    <CalendarEventsContext.Provider
      value={{
        events,
        getEventsByDate,
      }}
    >
      {children}
    </CalendarEventsContext.Provider>
  );
};

export const CalendarEventsConsumer = CalendarEventsContext.Consumer;

export const useCalendarEvents = () => useContext(CalendarEventsContext);
